import { Container, Graphics } from 'pixi.js';
import type { World, EntityId } from '@/core/ECS';
import { Hostile, Position, Renderable, Velocity } from '@/components';
import type { PredatorDef } from '@/data/types';
import { drawAmoeba, drawParamecium, drawDinoflagellate } from '@/visuals/bio';

/**
 * createPredator — hostile entity. Visuals dispatched per predatorId to
 * biology-themed draw routines (pseudopod amoeba, ciliate paramecium,
 * armored dinoflagellate).
 */
export interface PredatorStatMul {
  hp?: number;
  damage?: number;
  speed?: number;
  radius?: number;
}

export function createPredator(
  world: World,
  parent: Container,
  def: PredatorDef,
  x: number,
  y: number,
  mul: PredatorStatMul = {},
): EntityId {
  const e = world.createEntity();

  const hpM = mul.hp ?? 1;
  const dmgM = mul.damage ?? 1;
  const spdM = mul.speed ?? 1;
  const radM = mul.radius ?? 1;
  const radius = def.radius * radM;

  const root = new Container();
  root.position.set(x, y);
  const glow = new Graphics();
  const body = new Graphics();
  root.addChild(glow, body);
  parent.addChild(root);

  const draw = pickPredatorDraw(def, radius);
  draw(0, body);

  // Tint scale: bigger predators get a slightly darker, more saturated look so
  // late-game threats read visually as upgraded, not just bigger.
  if (radM > 1.05) root.tint = darken(0xffffff, Math.min(0.25, (radM - 1) * 1.2));

  world.addComponent(e, Position, { x, y });
  world.addComponent(e, Velocity, { vx: 0, vy: 0 });
  world.addComponent(e, Renderable, {
    root, body, glow, baseScale: 1, time: Math.random() * 10,
    draw: (t, b) => draw(t, b),
  });
  world.addComponent(e, Hostile, {
    predatorId: def.id,
    damage: def.damage * dmgM,
    radius,
    speed: def.speed * spdM,
    behavior: def.behavior,
    hp: def.hp * hpM,
    maxHp: def.hp * hpM,
    attackCooldown: def.attackCooldown,
    attackTimer: 0,
    ambushTimer: def.behavior === 'ambush' ? 1.5 : undefined,
    codexId: def.codexId,
    piercesShield: def.piercesShield,
    splitsInto: def.splitsInto,
  });

  return e;
}

function darken(_color: number, t: number): number {
  // simple value -> tint approximation (Pixi tint multiplies the texture)
  const v = Math.round(255 * (1 - t));
  return (v << 16) | (v << 8) | v;
}

function pickPredatorDraw(def: PredatorDef, radius: number): (time: number, body: Graphics) => void {
  const opts = { radius, color: def.color };
  switch (def.id) {
    case 'amoeba':
      return (t, b) => drawAmoeba(b, opts, t);
    case 'paramecium_hunter':
      return (t, b) => drawParamecium(b, opts, t);
    case 'dinoflagellate':
      return (t, b) => drawDinoflagellate(b, opts, t);
    case 'spore_swarm':
      // small fast variant — cilia-style movement reads well
      return (t, b) => drawParamecium(b, opts, t);
    case 'phage_pierce':
      // shield-piercer — amoeba shape in saturated red; pulse highlights danger
      return (t, b) => drawAmoeba(b, opts, t);
    case 'apex_phage':
      // boss-tier ambush — armored dinoflagellate look
      return (t, b) => drawDinoflagellate(b, opts, t);
    default:
      return (_t, b) => drawGenericPredator(b, def, radius);
  }
}

function drawGenericPredator(body: Graphics, def: PredatorDef, radius: number): void {
  body.clear();
  body.circle(0, 0, radius * 1.15).fill({ color: def.color, alpha: 0.3 });
  body.circle(0, 0, radius).fill({ color: def.color, alpha: 0.75 });
  body.circle(0, 0, radius * 0.35).fill({ color: 0xffffff, alpha: 0.3 });
  body.circle(0, 0, radius).stroke({ color: def.color, alpha: 0.9, width: 1.5 });
}
